import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, computed, contentChildren, effect, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { ButtonComponent } from '../../components/button/button.component';
import { ButtonContainerTemplateLayouts, ButtonContainerTemplateSizing } from './button-container-template.model';

/**
 * On phone-sized viewports the container always stacks a centered column of
 * fixed-size buttons. Matches the upper bound of the legacy flex-layout 'xs'
 * alias this component previously observed.
 */
const XS_QUERY = '(max-width: 599.98px)';

function coerceLayout(value: ButtonContainerTemplateLayouts | null | undefined): ButtonContainerTemplateLayouts {
  return value === 'row' || value === 'row-reverse' || value === 'column' ? value : 'column';
}

function coerceSizing(value: ButtonContainerTemplateSizing | undefined): ButtonContainerTemplateSizing {
  return value === 'fixedSize' || value === 'fullSize' ? value : null;
}

/**
 * Arranges content-projected buttons (and other content) into a responsive
 * layout, choosing direction, alignment, and button sizing from how many
 * buttons are projected:
 *
 * - one button → `preferredSingleLayout`
 * - two buttons → `preferredDualLayout`
 * - three or more → always a centered column
 * - phone-sized viewports → always a centered column of fixed-size buttons
 *
 * Unlike a Button Container, this component exposes a content slot, so the
 * host template controls which buttons exist (and can wrap them in `@if`).
 */
@Component({
  selector: 'app-button-container-template',
  templateUrl: './button-container-template.component.html',
  styleUrls: ['../button-container/button-container.component.scss', './button-container-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonContainerTemplateComponent {
  private readonly breakpointObserver = inject(BreakpointObserver);

  /**
   * The content-projected buttons. The layout reacts to how many there are,
   * including buttons appearing or disappearing behind `@if` blocks.
   */
  private readonly buttons = contentChildren(ButtonComponent);

  /**
   * Preferred Layout if there is a single button.
   */
  readonly preferredSingleLayout = input<ButtonContainerTemplateLayouts, ButtonContainerTemplateLayouts | null | undefined>('column', {
    transform: coerceLayout,
  });

  /**
   * Preferred Layout if there are two buttons.
   */
  readonly preferredDualLayout = input<ButtonContainerTemplateLayouts, ButtonContainerTemplateLayouts | null | undefined>('column', {
    transform: coerceLayout,
  });

  /**
   * Preferred Button sizing for Single Buttons.
   *
   * Do not use with Fab style buttons — sizing stretches them.
   */
  readonly preferredSingleButtonSizing = input<ButtonContainerTemplateSizing, ButtonContainerTemplateSizing | undefined>(null, {
    transform: coerceSizing,
  });

  /**
   * Is the viewport phone-sized?
   */
  private readonly isXs = toSignal(this.breakpointObserver.observe(XS_QUERY).pipe(map((state) => state.matches)), {
    initialValue: this.breakpointObserver.isMatched(XS_QUERY),
  });

  /**
   * The active layout: flex styles for the wrapper and the sizing pushed
   * down to the projected buttons.
   */
  protected readonly layoutState = computed(() => {
    const count = this.buttons().length;

    // an empty slot renders an empty wrapper; the values are irrelevant
    if (count === 0) {
      return { direction: 'column', justifyContent: 'flex-start', alignItems: 'stretch', sizing: null as ButtonContainerTemplateSizing };
    }

    // phone-sized viewports always stack a centered column of fixed buttons
    if (this.isXs()) {
      return { direction: 'column', justifyContent: 'center', alignItems: 'center', sizing: 'fixedSize' as const };
    }

    const sizing = this.preferredSingleButtonSizing();

    if (count === 1) {
      const layout = this.preferredSingleLayout();
      const isRow = layout === 'row' || layout === 'row-reverse';
      return {
        direction: layout,
        justifyContent: isRow ? 'flex-start' : 'center',
        alignItems: 'center',
        sizing,
      };
    }

    if (count === 2) {
      const layout = this.preferredDualLayout();
      const isRow = layout === 'row' || layout === 'row-reverse';
      return {
        direction: layout,
        justifyContent: isRow ? 'space-between' : 'flex-start',
        // A stacked pair centers like every other stacked layout (one button,
        // three-plus, the phone column, and all Button Container layouts) —
        // fixed-size buttons keep their width cap and sit mid-column instead
        // of hugging the left edge.
        alignItems: 'center',
        sizing,
      };
    }

    // three or more buttons can only stack in a centered column
    return { direction: 'column', justifyContent: 'center', alignItems: 'center', sizing };
  });

  constructor() {
    // push the active sizing down to every projected button
    effect(() => {
      const sizing = this.layoutState().sizing;
      for (const button of this.buttons()) {
        button.isFixedSize = sizing === 'fixedSize';
        button.isFullSize = sizing === 'fullSize';
      }
    });
  }
}
